/**
 * Pro Design Tokens — theming for the subscription / Deep Dive surfaces.
 *
 * Every measurement here is expressed in `rem` via `r()` from the token
 * module. The app ships inside iOS and Android WebViews, which honour the OS
 * text-size setting: a px measurement does not scale with it, so containers
 * overflow when a user enlarges their display. That applies to hairlines and
 * shadow offsets too — a border that does not scale with its text disappears
 * against that text at 2× (STACK-495).
 *
 * The colour ramp below was the last legacy piece, and Phase Sweep batch 7
 * (STACK-509) retired it: every value now resolves to a brief-locked token.
 * The earlier phase had already removed the iOS blue, which was the one value
 * actively teaching new surfaces the wrong action colour.
 */

import { alpha } from '@mui/material/styles';

import { r, color, radius } from '../../theme/tokens';

export const proDesignTokens = {
  // Colors — brand neutrals with the signal accent
  colors: {
    pro: {
      accent: color.accent,
      accentLight: alpha(color.accent, 0.08),
      accentMedium: alpha(color.accent, 0.2),
    },
    // A ten-step Tailwind grey ramp mapped onto the brief's neutrals, which
    // are six values rather than ten — so steps collapse in pairs (200/300,
    // 500/600) and the three darkest all land on `ink`. That is the brief
    // working: it locks one body ink and one hairline, not a ramp. The keys
    // stay because `article/` and `subscription/` surfaces index them too.
    neutral: {
      50: color.paper,
      100: color.surface,
      200: color.line,
      300: color.line,
      400: color.inkMuted,
      500: color.inkSoft,
      600: color.inkSoft,
      700: color.ink,
      800: color.ink,
      900: color.ink,
    },
    border: {
      subtle: 'rgba(0, 0, 0, 0.06)',
      medium: 'rgba(0, 0, 0, 0.1)',
      strong: 'rgba(0, 0, 0, 0.2)',
    },
    background: {
      subtle: 'rgba(0, 0, 0, 0.02)',
      medium: 'rgba(0, 0, 0, 0.04)',
    }
  },

  // Typography - SF Pro inspired
  typography: {
    proBadge: {
      fontSize: '0.6875rem', // 11px
      fontWeight: 500,
      letterSpacing: r(0.3),
      lineHeight: 1.2,
    },
    proHeading: {
      fontSize: '1.125rem', // 18px
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.3,
    },
    proBody: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: 1.5,
    }
  },

  // Shadows — subtle elevations, offsets in rem so they scale with text
  shadows: {
    subtle: `0 ${r(1)} ${r(3)} rgba(0, 0, 0, 0.08)`,
    medium: `0 ${r(2)} ${r(8)} rgba(0, 0, 0, 0.12)`,
    strong: `0 ${r(4)} ${r(16)} rgba(0, 0, 0, 0.16)`,
    proBadge: `0 ${r(1)} ${r(2)} rgba(0, 0, 0, 0.1)`,
    proCard: `0 ${r(1)} ${r(3)} rgba(0, 0, 0, 0.08), 0 ${r(1)} ${r(2)} rgba(0, 0, 0, 0.06)`,
    proCardHover: `0 ${r(2)} ${r(8)} rgba(0, 0, 0, 0.12), 0 ${r(1)} ${r(3)} rgba(0, 0, 0, 0.08)`,
  },

  // Spacing - 4px base unit for consistency
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '0.75rem',   // 12px
    lg: '1rem',      // 16px
    xl: '1.5rem',    // 24px
    xxl: '2rem',     // 32px
  },

  // Border radius — the brief locks three values (8 / 12 / 16). The 4px `sm`
  // and `badge` entries were outside that set and now round up to 8; `sm` and
  // `md` collapsing onto the same value is the lock working, not a mistake.
  borderRadius: {
    sm: radius.sm,    // 8px
    md: radius.sm,    // 8px
    lg: radius.md,    // 12px
    xl: radius.lg,    // 16px
    badge: radius.sm, // 8px for badges
    card: radius.md,  // 12px for cards
  },

  // Transitions - Smooth, natural animations
  transitions: {
    fast: '150ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    medium: '200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    slow: '300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    spring: '300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  },

  // Component-specific tokens
  components: {
    proBadge: {
      padding: '0.125rem 0.375rem', // 2px 6px
      background: 'rgba(0, 0, 0, 0.04)',
      border: `${r(1)} solid rgba(0, 0, 0, 0.1)`,
      borderRadius: radius.sm,
    },
    proCard: {
      padding: '1rem', // 16px
      background: color.paper,
      border: `${r(1)} solid rgba(0, 0, 0, 0.06)`,
      borderRadius: radius.md,
    },
    lockedOverlay: {
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: `blur(${r(1)})`,
    },
    // Was iOS blue — the value that taught every new subscription surface the
    // wrong action colour. The brief locks a single action colour, the signal
    // accent, and puts dark ink on it: white on accent fails AA at body size
    // (brief §9), and dark-on-accent is a deliberate differentiator (§10).
    //
    // Recoloured rather than deleted: `TrialExpiredBanner` and
    // `ComplimentaryExpiredBanner` both spread this.
    primaryButton: {
      background: color.accent,
      color: color.accentInk,
      fontWeight: 600,
      borderRadius: radius.sm,
      textTransform: 'none',
      transition: '200ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      '&:hover': {
        background: color.accentHover,
        transform: `translateY(-${r(1)})`,
        boxShadow: `0 ${r(2)} ${r(8)} rgba(0, 0, 0, 0.12)`,
      },
      '&:disabled': {
        background: color.line,
        color: color.inkSoft,
      }
    }
  }
};

export default proDesignTokens; 